import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import DashboardService from '../services/dashboardService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function displayName(user) {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return full || user.email;
}

function roleBadge(role) {
  if (role === 'nonprofit_admin') {
    return (
      <span className="ml-1 px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700 font-medium">
        Admin
      </span>
    );
  }
  return (
    <span className="ml-1 px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700 font-medium">
      Volunteer
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// History Panel
// ---------------------------------------------------------------------------

const EmailHistory = ({ onClose }) => {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewSend, setPreviewSend] = useState(null); // send object to preview

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { sends: data } = await DashboardService.getEmailHistory();
        setSends(data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow flex flex-col" style={{ minHeight: '70vh' }}>
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Sent Emails</h3>
        <button onClick={onClose} className="text-sm text-blue-600 hover:underline">
          + Compose new
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="p-6 text-sm text-red-600">{error}</div>
      ) : sends.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          No emails sent yet.
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Sent by</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Delivered</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Failed</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sends.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium text-gray-900 max-w-xs truncate">{s.subject}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {s.sent_by_email || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(s.created_at)}</td>
                  <td className="px-4 py-3 text-right text-green-700 font-medium">{s.sent_count}</td>
                  <td className="px-4 py-3 text-right">
                    {s.failed_count > 0
                      ? <span className="text-red-600 font-medium">{s.failed_count}</span>
                      : <span className="text-gray-400">0</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setPreviewSend(s)}
                      className="text-blue-600 hover:underline text-xs"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview modal */}
      {previewSend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl flex flex-col w-full max-w-3xl" style={{ maxHeight: '90vh' }}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-gray-900">{previewSend.subject}</h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  Sent {new Date(previewSend.created_at).toLocaleString()} &bull;{' '}
                  {previewSend.sent_count} delivered
                  {previewSend.failed_count > 0 && `, ${previewSend.failed_count} failed`}
                </p>
                {previewSend.recipient_emails && previewSend.recipient_emails.length > 0 && (
                  <details className="mt-1">
                    <summary className="text-xs text-blue-600 cursor-pointer">
                      {previewSend.recipient_emails.length} recipients
                    </summary>
                    <p className="text-xs text-gray-600 mt-1 max-h-24 overflow-y-auto">
                      {previewSend.recipient_emails.join(', ')}
                    </p>
                  </details>
                )}
              </div>
              <button
                onClick={() => setPreviewSend(null)}
                className="ml-4 text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <iframe
                srcDoc={previewSend.html_content}
                title="Email preview"
                sandbox="allow-same-origin"
                className="w-full h-full rounded border border-gray-200"
                style={{ minHeight: '500px', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main composer
// ---------------------------------------------------------------------------

const SuperAdminEmailComposer = () => {
  // --- view ---
  const [view, setView] = useState('compose'); // 'compose' | 'history'

  // --- data ---
  const [orgs, setOrgs] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [recipientsError, setRecipientsError] = useState('');

  // --- compose ---
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [activeTab, setActiveTab] = useState('write'); // 'write' | 'preview'

  // --- recipient picker ---
  const [roleFilter, setRoleFilter] = useState('all'); // 'all' | 'volunteer' | 'nonprofit_admin'
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());
  const [selectedEmails, setSelectedEmails] = useState(new Set());

  // --- send ---
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { sent, failed, errors }

  const previewRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Load recipients
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingRecipients(true);
        setRecipientsError('');
        const { orgs: data } = await DashboardService.getEmailRecipients();
        setOrgs(data || []);
      } catch (err) {
        setRecipientsError(err.message);
      } finally {
        setLoadingRecipients(false);
      }
    };
    load();
  }, []);

  // ---------------------------------------------------------------------------
  // Filtered user lists per org
  // ---------------------------------------------------------------------------

  const filteredOrgs = useMemo(() => {
    return orgs.map((org) => ({
      ...org,
      users: org.users.filter(
        (u) => roleFilter === 'all' || u.role === roleFilter
      ),
    })).filter((org) => org.users.length > 0);
  }, [orgs, roleFilter]);

  const allFilteredEmails = useMemo(() => {
    return filteredOrgs.flatMap((org) => org.users.map((u) => u.email));
  }, [filteredOrgs]);

  // ---------------------------------------------------------------------------
  // Selection helpers
  // ---------------------------------------------------------------------------

  const isOrgFullySelected = (org) =>
    org.users.length > 0 && org.users.every((u) => selectedEmails.has(u.email));

  const isOrgPartiallySelected = (org) =>
    org.users.some((u) => selectedEmails.has(u.email)) && !isOrgFullySelected(org);

  const toggleOrg = (org) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (isOrgFullySelected(org)) {
        org.users.forEach((u) => next.delete(u.email));
      } else {
        org.users.forEach((u) => next.add(u.email));
      }
      return next;
    });
  };

  const toggleUser = (email) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const selectAll = () => setSelectedEmails(new Set(allFilteredEmails));
  const clearAll = () => setSelectedEmails(new Set());

  const toggleExpand = (orgId) => {
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Send
  // ---------------------------------------------------------------------------

  const handleSend = async () => {
    setSending(true);
    setSendResult(null);
    try {
      // Build email → firstName lookup from loaded org data
      const nameMap = {};
      orgs.forEach((org) => org.users.forEach((u) => { nameMap[u.email] = u.firstName || ''; }));

      const recipients = [...selectedEmails].map((email) => ({
        email,
        firstName: nameMap[email] || '',
      }));

      const result = await DashboardService.sendMassEmail({
        subject,
        html: htmlContent,
        recipients,
      });
      setSendResult(result);
    } catch (err) {
      setSendResult({ success: false, message: err.message });
    } finally {
      setSending(false);
      setShowConfirm(false);
    }
  };

  const canSend = subject.trim() && htmlContent.trim() && selectedEmails.size > 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (view === 'history') {
    return <EmailHistory onClose={() => setView('compose')} />;
  }

  return (
    <div className="space-y-4">
    {/* View toggle */}
    <div className="flex justify-end">
      <button
        onClick={() => setView('history')}
        className="text-sm text-blue-600 hover:underline"
      >
        View sent history
      </button>
    </div>
    <div className="flex gap-6 h-full" style={{ minHeight: '70vh' }}>

      {/* ------------------------------------------------------------------ */}
      {/* LEFT: Recipient Picker                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="w-80 flex-shrink-0 bg-white rounded-lg shadow flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Recipients</h3>

          {/* Role filter */}
          <div className="flex gap-1 mt-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'volunteer', label: 'Volunteers' },
              { value: 'nonprofit_admin', label: 'Admins' },
            ].map((f) => (
              <button
                key={f.value}
                onClick={() => setRoleFilter(f.value)}
                className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                  roleFilter === f.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Select all / clear */}
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-gray-500">
              {selectedEmails.size} of {allFilteredEmails.length} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs text-blue-600 hover:underline"
              >
                Select all
              </button>
              <button
                onClick={clearAll}
                className="text-xs text-gray-500 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Org list */}
        <div className="flex-1 overflow-y-auto">
          {loadingRecipients ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
            </div>
          ) : recipientsError ? (
            <div className="p-4 text-sm text-red-600">{recipientsError}</div>
          ) : filteredOrgs.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">No recipients match this filter.</div>
          ) : (
            filteredOrgs.map((org) => {
              const expanded = expandedOrgs.has(org.id);
              const fullySelected = isOrgFullySelected(org);
              const partial = isOrgPartiallySelected(org);

              return (
                <div key={org.id} className="border-b border-gray-100 last:border-0">
                  {/* Org row */}
                  <div className="flex items-center px-3 py-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={fullySelected}
                      ref={(el) => {
                        if (el) el.indeterminate = partial;
                      }}
                      onChange={() => toggleOrg(org)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 mr-2 flex-shrink-0"
                    />
                    <button
                      onClick={() => toggleExpand(org.id)}
                      className="flex-1 flex items-center justify-between text-left min-w-0"
                    >
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-800 truncate block">
                          {org.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {org.users.length} {org.users.length === 1 ? 'contact' : 'contacts'}
                        </span>
                      </div>
                      <span className="text-gray-400 ml-2 flex-shrink-0">
                        {expanded ? '▾' : '▸'}
                      </span>
                    </button>
                  </div>

                  {/* User rows */}
                  {expanded && (
                    <div className="bg-gray-50">
                      {org.users.map((user) => (
                        <label
                          key={user.email}
                          className="flex items-start px-6 py-1.5 hover:bg-gray-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedEmails.has(user.email)}
                            onChange={() => toggleUser(user.email)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 mr-2 flex-shrink-0 mt-0.5"
                          />
                          <div className="min-w-0">
                            <div className="flex items-center flex-wrap">
                              <span className="text-xs font-medium text-gray-700">
                                {displayName(user)}
                              </span>
                              {roleBadge(user.role)}
                            </div>
                            <span className="text-xs text-gray-500 truncate block">
                              {user.email}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* RIGHT: Compose                                                        */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex-1 bg-white rounded-lg shadow flex flex-col">
        {/* Subject */}
        <div className="px-6 py-4 border-b border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject line..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Write / Preview tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => setActiveTab('write')}
            className={`py-2 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'write'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Write
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`py-2 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'preview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Preview
          </button>
        </div>

        {/* Editor / Preview area */}
        <div className="flex-1 p-4 overflow-hidden flex flex-col">
          {activeTab === 'write' ? (
            <textarea
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              placeholder="Paste your HTML email content here..."
              className="flex-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              style={{ minHeight: '400px' }}
            />
          ) : (
            <div className="flex-1 border border-gray-200 rounded-md overflow-hidden bg-gray-50">
              {htmlContent ? (
                <iframe
                  ref={previewRef}
                  srcDoc={htmlContent}
                  title="Email preview"
                  sandbox="allow-same-origin"
                  className="w-full h-full"
                  style={{ minHeight: '400px', border: 'none' }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  Nothing to preview — write some HTML first.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedEmails.size > 0 ? (
              <span>
                Sending to <strong>{selectedEmails.size}</strong>{' '}
                {selectedEmails.size === 1 ? 'recipient' : 'recipients'}
              </span>
            ) : (
              <span className="text-gray-400">No recipients selected</span>
            )}
          </div>
          <button
            onClick={() => setSendResult(null) || setShowConfirm(true)}
            disabled={!canSend}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
              canSend
                ? 'bg-primary-600 hover:bg-primary-700 text-white'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Send Email
          </button>
        </div>

        {/* Send result banner */}
        {sendResult && (
          <div
            className={`mx-6 mb-4 px-4 py-3 rounded-md text-sm ${
              sendResult.success
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {sendResult.message || (sendResult.success
              ? `Sent: ${sendResult.sent} succeeded, ${sendResult.failed} failed.`
              : 'Send failed.')}
            {sendResult.errors && sendResult.errors.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer">Show failures ({sendResult.errors.length})</summary>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {sendResult.errors.map((e, i) => (
                    <li key={i}>{e.email}: {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Confirm Modal                                                         */}
      {/* ------------------------------------------------------------------ */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Send</h3>
            <p className="text-sm text-gray-600 mb-1">
              You are about to send <strong>"{subject}"</strong> to{' '}
              <strong>{selectedEmails.size} recipient{selectedEmails.size !== 1 ? 's' : ''}</strong>.
            </p>
            <p className="text-sm text-gray-500 mb-4">This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-60"
              >
                {sending ? 'Sending...' : 'Yes, Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default SuperAdminEmailComposer;
